import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, Loader2, Trash2 } from "lucide-react";

/**
 * V1.26 · Diálogo de eliminación definitiva de alumno.
 *
 * Flujo:
 *   1. Al abrir, consulta GET /students/{id}/deletion-check y muestra
 *      cualquier bloqueo (facturas, clases futuras, firmas, etc.).
 *   2. Si `can_delete === true`, permite introducir motivo + PIN maestro
 *      y confirmar.
 *   3. Envía DELETE /students/{id} con `x-sudo-token` = PIN. El backend
 *      exige además `is_super_admin` en el usuario actor.
 *
 * Diseño: paleta roja + iconos de aviso. Nunca abre por accidente ni permite
 * eliminar mientras hay bloqueos.
 */
const BLOCKER_LABELS = {
  pending_invoices: "Facturas pendientes o impagadas",
  future_appointments: "Clases futuras programadas",
  pending_signatures: "Firmas pendientes",
  dgt_awaiting_signature: "Documentos DGT esperando firma",
  dgt_draft_pending: "Solicitudes DGT en borrador",
  contracts_draft: "Contratos en borrador",
};

export default function DeleteStudentDialog({ open, student, onClose, onDeleted }) {
  const [checking, setChecking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [check, setCheck] = useState(null);
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open || !student?.id) return;
    let cancelled = false;
    setCheck(null);
    setPin("");
    setReason("");
    setChecking(true);
    api
      .get(`/students/${student.id}/deletion-check`)
      .then(({ data }) => {
        if (!cancelled) setCheck(data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(formatApiError(err?.response?.data?.detail));
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, student?.id]);

  const doDelete = useCallback(async () => {
    if (!student?.id) return;
    if (!pin.trim()) {
      toast.error("Introduce el PIN de administrador");
      return;
    }
    setDeleting(true);
    try {
      // V1.26 · Paso 1: cambiar el PIN por un sudo_token JWT firmado.
      let sudoToken;
      try {
        const { data: sudoData } = await api.post("/security/pin/verify", {
          pin: pin.trim(),
          operation: "student.delete_permanent",
        });
        sudoToken = sudoData.sudo_token;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 423) {
          toast.error(err?.response?.data?.detail || "PIN incorrecto");
        } else {
          toast.error(formatApiError(err?.response?.data?.detail));
        }
        return;
      }
      // V1.26 · Paso 2: llamar al DELETE con el sudo_token.
      const { data } = await api.delete(`/students/${student.id}`, {
        headers: { "x-sudo-token": sudoToken },
        data: { reason: reason.trim() },
      });
      toast.success(
        `Alumno eliminado definitivamente. ${
          data.signed_documents_preserved
            ? `${data.signed_documents_preserved} documento(s) firmado(s) preservado(s) en auditoría.`
            : ""
        }`,
      );
      if (data.had_onedrive && data.onedrive_notice) {
        toast.warning(data.onedrive_notice);
      }
      onDeleted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "deletion_blocked") {
        toast.error("La eliminación está bloqueada — hay procesos pendientes.");
        setCheck((prev) => ({ ...(prev || {}), blockers: detail.blockers, can_delete: false }));
      } else if (err?.response?.status === 403) {
        toast.error("Se requiere permiso de super_admin para eliminar alumnos.");
      } else if (err?.response?.status === 440) {
        toast.error("Sesión sudo caducada. Vuelve a introducir el PIN.");
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setDeleting(false);
    }
  }, [student?.id, pin, reason, onDeleted]);

  if (!student) return null;
  const canDelete = check?.can_delete === true;
  const activeBlockers = check?.blockers
    ? Object.entries(check.blockers).filter(([, v]) => v > 0)
    : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="delete-student-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-800">
            <ShieldAlert className="h-5 w-5" />
            ATENCIÓN — Eliminar alumno definitivamente
          </DialogTitle>
          <DialogDescription>
            Va a eliminar <strong>{student.name}</strong> del sistema. La
            documentación archivada en OneDrive NO será eliminada. Esta
            acción no puede deshacerse.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {checking && (
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verificando procesos abiertos…
            </div>
          )}

          {!checking && check && activeBlockers.length > 0 && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="delete-blockers-panel"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold">
                    Este alumno tiene procesos abiertos — la eliminación está
                    bloqueada:
                  </p>
                  <ul className="mt-2 space-y-0.5 list-disc list-inside">
                    {activeBlockers.map(([key, count]) => (
                      <li
                        key={key}
                        data-testid={`blocker-${key}`}
                        className="text-[13px]"
                      >
                        <strong>{count}</strong> · {BLOCKER_LABELS[key] || key}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] mt-2 text-amber-800">
                    Resuelve estos procesos (o cancela/inactiva los borradores)
                    antes de intentarlo de nuevo.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!checking && check && canDelete && (
            <>
              {check.has_onedrive && (
                <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900">
                  <p className="font-semibold">Aviso · OneDrive</p>
                  <p className="text-[13px] mt-1">
                    Este alumno tiene documentación archivada en OneDrive. Los
                    archivos <strong>NO se eliminarán</strong>; debes gestionarlos
                    manualmente desde el portal de OneDrive si es necesario.
                  </p>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs font-semibold text-neutral-700">
                  Motivo (opcional, se registra en la auditoría)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="delete-reason-input"
                  placeholder="Ej: duplicado erróneo, alumno anulado, etc."
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-neutral-700">
                  PIN ADMINISTRADOR *
                </label>
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  data-testid="delete-pin-input"
                  autoComplete="off"
                  placeholder="Introduce el PIN maestro"
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            data-testid="delete-cancel-btn"
            className="btn-ghost border border-neutral-300 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={doDelete}
            disabled={!canDelete || deleting || checking || !pin.trim()}
            data-testid="delete-confirm-btn"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {deleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Eliminar definitivamente
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
